import { AxiosResponse } from 'axios-cad-nx';
import {
    AgencyEvent,
    ApiResultAgencyEventView,
    ConsolidatedLocationView,
    CopyCallResource,
    CopyCallView,
} from '@mark43/cad-api';
import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { CopyCallFormDisplayType } from '../components/copy-call-form';

interface CopyCallApi {
    copyCall(data: CopyCallFormDisplayType): void;
    copyCallFormData: CopyCallView | undefined;
    query: ReturnType<typeof useQuery>;
    mutation: UseMutationResult<
        AxiosResponse<ApiResultAgencyEventView, any>,
        unknown,
        CopyCallFormDisplayType,
        unknown
    >;
}

export function useCopyCallApi(
    agencyEventId: number,
    onSuccess: (resp: AgencyEvent) => void
): CopyCallApi {
    const apiClient = useApiClient();

    const mutation = useMutation(
        ['copy-call', {}],
        (form: CopyCallFormDisplayType) => {
            return apiClient.request<CopyCallResource.CopyCallV2>({
                method: 'POST',
                url: 'copy_call',
                data: {
                    ...form,
                    consolidatedLocationView:
                        form.consolidatedLocationView as ConsolidatedLocationView,
                    useCurrentDateTime: form.useCurrentDateTime === 'true' ? true : false,
                },
            });
        },
        {
            onSuccess(data) {
                if (data.data.data) {
                    onSuccess(data.data.data);
                }
            },
        }
    );

    const { mutate } = mutation;

    const query = useQuery(
        ['copy-call-form-data', agencyEventId],
        () => {
            return apiClient
                .request<CopyCallResource.CopyCallBootstrap>({
                    method: 'GET',
                    url: `copy_call/${agencyEventId}/`,
                })
                .then((resp) => resp.data.data);
        },
        { refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false }
    );

    const { data: copyCallFormData } = query;

    // Since we don't classify forced locations, we need to manually insert a dummy entity link.
    function insertEntityLinkForForcedLocation(
        data: CopyCallFormDisplayType
    ): CopyCallFormDisplayType {
        const location = data.consolidatedLocationView;
        if (!location?.areCoordinatesVerified && location?.entityLinks.length === 0) {
            return {
                ...data,
                consolidatedLocationView: {
                    ...data.consolidatedLocationView,
                    entityLinks: [
                        // @ts-expect-error This is valid for a forced location.
                        {},
                    ],
                },
            };
        }

        return data;
    }

    function copyCall(data: CopyCallFormDisplayType): void {
        mutate(insertEntityLinkForForcedLocation(data));
    }

    return {
        query,
        mutation,
        copyCall,
        copyCallFormData,
    };
}
